import { Box, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { AiOutlineWarning } from 'react-icons/ai';
import { CiRedo, CiWifiOff } from 'react-icons/ci';
import { IoClose, IoWifiSharp } from 'react-icons/io5';
import { MdCheckCircleOutline, MdTune } from 'react-icons/md';
import Button from 'components/Common/Button';

const TROUBLESHOOTING_TIPS = [
  'Credentials are copied correctly with no extra spaces',
  'Credentials have not expired or been revoked',
  'Selected location matches the credential scope',
  'Your account has API access enabled for this integration'
];

type Props = {
  open: boolean;
  status: 'validating' | 'success' | 'error';
  integrationName: string;
  errorMessage?: string;
  disableClose?: boolean;
  onClose: () => void;
  onEditCredentials?: () => void;
  onRetry?: () => void;
};

export default function IntegrationCredentialModal({
  open,
  status,
  integrationName,
  errorMessage,
  disableClose = false,
  onClose,
  onEditCredentials,
  onRetry
}: Props) {
  const theme = useTheme();
  const blueMain = theme.palette.blue?.main ?? '#1e40af';

  const handleClose = disableClose ? undefined : onClose;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxWidth: 520 } }}
    >
      {!disableClose && (
        <IconButton
          aria-label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary', zIndex: 1 }}
        >
          <IoClose size={22} />
        </IconButton>
      )}

      {status === 'validating' && (
        <DialogContent sx={{ textAlign: 'center', py: 5, px: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: theme.palette.blue?.light ?? '#eef5ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <IoWifiSharp size={36} color={blueMain} />
          </Box>
          <Typography fontSize={20} fontWeight={500} mb={1}>
            Validating Credentials
          </Typography>
          <Typography fontSize={14} color='text.secondary' lineHeight={1.5}>
            Securely connecting to {integrationName}. This usually takes a few seconds...
          </Typography>
        </DialogContent>
      )}

      {status === 'success' && (
        <DialogContent sx={{ textAlign: 'center', py: 5, px: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: theme.palette.blue?.light ?? '#eef5ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <MdCheckCircleOutline size={44} color={blueMain} />
          </Box>
          <Typography fontSize={20} fontWeight={500} mb={1}>
            Connection Verified
          </Typography>
          <Typography fontSize={14} color='text.secondary'>
            Successfully connected to {integrationName}.
          </Typography>
        </DialogContent>
      )}

      {status === 'error' && (
        <>
          <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2, px: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: theme.palette.error.light,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}
            >
              <CiWifiOff size={36} color={theme.palette.error.main} />
            </Box>
            <Typography fontSize={20} fontWeight={500} mb={1}>
              Couldn&apos;t connect to {integrationName}
            </Typography>
            <Typography fontSize={14} color='text.secondary' mb={2.5} lineHeight={1.5}>
              We tried to validate your credentials but the connection was rejected.
            </Typography>

            <Box
              sx={{
                bgcolor: theme.palette.error.light,
                border: `1px solid ${theme.palette.error.main}`,
                borderRadius: '8px',
                p: '12px 16px',
                textAlign: 'left',
                mb: 2.5
              }}
            >
              <Stack direction='row' alignItems='center' gap={0.75} mb={1}>
                <AiOutlineWarning size={16} color={theme.palette.error.main} />
                <Typography fontSize={14} fontWeight={600} color='error.main'>
                  401 — Authentication failed
                </Typography>
              </Stack>
              <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
                <Typography component='li' fontSize={13} color='error.main' lineHeight={1.5}>
                  {errorMessage || 'Integration authentication failed. Please check your credentials and update.'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: 'left' }}>
              <Typography fontSize={14} fontWeight={600} mb={1}>
                Things to check:
              </Typography>
              <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
                {TROUBLESHOOTING_TIPS.map((tip) => (
                  <Typography key={tip} component='li' fontSize={13} color='text.secondary' lineHeight={1.6} mb={0.25}>
                    {tip}
                  </Typography>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
            <Button variant='outlined' color='secondary' startIcon={<MdTune size={18} />} onClick={onEditCredentials}>
              Edit Credentials
            </Button>
            <Button variant='contained' startIcon={<CiRedo size={18} />} onClick={onRetry}>
              Retry
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
